import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { debounce } from '@theme/utilities';

const STORAGE_KEY = 'tgp:frete-cep';

/** Alterar quantidade dispara vários re-renders; sem isso o leitor de tela vira metralhadora. */
const ATRASO_ANUNCIO = 1000;

/**
 * Faixas de CEP (valor numérico) que pertencem a Sul e Sudeste.
 * SP/RJ/ES/MG => 01000-000 a 39999-999
 * PR/SC/RS    => 80000-000 a 99999-999
 * Qualquer CEP fora dessas faixas cai na regra "demais regiões".
 */
const FAIXAS_SUL_SUDESTE = [
  [1000000, 39999999],
  [80000000, 99999999],
];

/**
 * Barra de progresso de frete grátis do carrinho.
 *
 * O cálculo do limite é local e determinístico (faixa de CEP -> região), então
 * funciona sem rede. A consulta ao ViaCEP é apenas enriquecimento: valida o CEP
 * e permite nomear a cidade. Se falhar, a barra continua funcionando.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [idle] - Estado inicial, com a isca.
 * @property {HTMLFormElement} [form] - Formulário de CEP.
 * @property {HTMLInputElement} [input] - Campo de CEP.
 * @property {HTMLElement} [error] - Mensagem de erro do CEP.
 * @property {HTMLElement} [result] - Estado com a barra de progresso.
 * @property {HTMLElement} [bar] - Elemento preenchido da barra.
 * @property {HTMLElement} [track] - Trilho da barra (dono do role=progressbar).
 * @property {HTMLElement} [message] - Texto acima da barra.
 * @property {HTMLElement} [place] - Rótulo "Entregas para o CEP: ...".
 * @property {HTMLElement} [announcer] - Região própria de anúncio para leitores de tela.
 *
 * @extends {Component<Refs>}
 */
class CartFreightBar extends Component {
  static observedAttributes = ['data-cart-total'];

  /** @type {AbortController | null} */
  #activeLookup = null;

  /** @type {boolean | null} Último patamar anunciado, para só falar quando ele muda. */
  #ultimoPatamar = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);

    const armazenado = this.#lerArmazenado();
    if (armazenado) this.#mostrarResultado(armazenado);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#activeLookup?.abort();
  }

  /**
   * O morph reescreve `data-cart-total` a cada atualização do carrinho, mas o
   * conteúdo interno é preservado por `data-skip-subtree-update`. Reagimos pelo
   * atributo em vez de depender do ciclo de vida do morph.
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'data-cart-total' || oldValue === newValue) return;

    const armazenado = this.#lerArmazenado();
    if (armazenado) this.#mostrarResultado(armazenado);
  }

  /** @type {number} Total do carrinho em centavos. */
  get totalCarrinho() {
    return Number(this.dataset.cartTotal ?? 0);
  }

  get limiteSulSudeste() {
    return Number(this.dataset.limitSouth ?? 0);
  }

  get limiteDemais() {
    return Number(this.dataset.limitOther ?? 0);
  }

  get formatoMoeda() {
    return this.dataset.moneyFormat ?? '';
  }

  get moeda() {
    return this.dataset.currency ?? 'BRL';
  }

  /**
   * Abre o formulário de CEP a partir da isca.
   */
  abrirFormulario = () => {
    this.dataset.state = 'form';
    this.refs.idle?.setAttribute('aria-expanded', 'true');
    // Foco só depois da troca de estado, para não rolar o drawer bruscamente.
    requestAnimationFrame(() => this.refs.input?.focus());
  };

  /**
   * Volta ao formulário para trocar o CEP já informado.
   */
  alterarCep = () => {
    const armazenado = this.#lerArmazenado();
    this.dataset.state = 'form';

    if (this.refs.input && armazenado) {
      this.refs.input.value = this.#formatarCep(armazenado.cep);
    }

    requestAnimationFrame(() => this.refs.input?.select());
  };

  /**
   * Máscara progressiva 00000-000 enquanto o cliente digita.
   * @param {InputEvent} event
   */
  aoDigitar = (event) => {
    const alvo = event.target;
    if (!(alvo instanceof HTMLInputElement)) return;

    const digitos = alvo.value.replace(/\D/g, '').slice(0, 8);
    const formatado = this.#formatarCep(digitos);

    if (alvo.value !== formatado) {
      // Reatribuir `value` joga o caret para o fim do campo. Reposicionamos
      // contando DÍGITOS antes do cursor, não caracteres, senão o hífen que a
      // máscara insere desloca o cursor em relação ao texto.
      const posicao = alvo.selectionStart ?? formatado.length;
      const digitosAntes = alvo.value.slice(0, posicao).replace(/\D/g, '').length;

      alvo.value = formatado;

      const nova = this.#posicaoDoDigito(formatado, digitosAntes);
      alvo.setSelectionRange(nova, nova);
    }

    if (this.refs.error) this.refs.error.hidden = true;

    // Auto-submit ao completar: o botão fica como afordância e apoio de teclado,
    // mas quem digita normalmente não precisa tocar nele.
    if (digitos.length === 8) this.calcular(event);
  };

  /**
   * Valida o CEP, resolve a região e mostra a barra.
   * @param {SubmitEvent} event
   */
  calcular = async (event) => {
    event.preventDefault();

    const digitos = (this.refs.input?.value ?? '').replace(/\D/g, '');

    if (digitos.length !== 8) {
      this.#mostrarErro('Digite um CEP com 8 dígitos.');
      return;
    }

    // O limite não depende da rede: resolve na hora pela faixa numérica.
    const dados = { cep: digitos, cidade: '', uf: '' };
    this.#mostrarResultado(dados);
    this.#gravar(dados);

    // O campo sai da tela ao virar barra; sem isso o teclado do iOS fica aberto
    // cobrindo o drawer, sem nada para digitar.
    this.refs.input?.blur();

    // Enriquecimento opcional: nome da cidade e validação de existência.
    this.#activeLookup?.abort();
    const controller = new AbortController();
    this.#activeLookup = controller;

    try {
      const resposta = await fetch(`https://viacep.com.br/ws/${digitos}/json/`, {
        signal: controller.signal,
      });
      if (!resposta.ok) return;

      const corpo = await resposta.json();
      if (corpo.erro) return;

      const enriquecido = {
        cep: digitos,
        cidade: corpo.localidade ?? '',
        uf: corpo.uf ?? '',
      };
      this.#gravar(enriquecido);
      this.#mostrarResultado(enriquecido);
    } catch (_) {
      // Sem rede ou CEP não encontrado: a barra já está correta pelo cálculo local.
    } finally {
      this.#activeLookup = null;
    }
  };

  /**
   * O nó é congelado no morph, então o total não chega mais pelo servidor: ele
   * vem do evento. Quando o evento não traz o carrinho, buscamos a fonte oficial.
   * @param {CustomEvent} event
   */
  #onCartUpdate = async (event) => {
    const armazenado = this.#lerArmazenado();
    const total = event.detail?.resource?.total_price;

    if (typeof total === 'number') {
      this.dataset.cartTotal = String(total);
    } else {
      try {
        const resposta = await fetch(`${Theme.routes.cart_url}.js`);
        const carrinho = await resposta.json();
        if (typeof carrinho?.total_price === 'number') {
          this.dataset.cartTotal = String(carrinho.total_price);
        }
      } catch (_) {
        // Mantém o último total conhecido em vez de zerar a barra.
      }
    }

    if (armazenado) this.#mostrarResultado(armazenado);
  };

  /**
   * Decide o limite de frete grátis a partir da faixa numérica do CEP.
   * @param {string} cep - 8 dígitos, sem máscara.
   * @returns {{ limite: number, sulSudeste: boolean }}
   */
  #resolverLimite(cep) {
    const numero = parseInt(cep, 10);
    const sulSudeste = FAIXAS_SUL_SUDESTE.some(([inicio, fim]) => numero >= inicio && numero <= fim);

    return {
      limite: sulSudeste ? this.limiteSulSudeste : this.limiteDemais,
      sulSudeste,
    };
  }

  /**
   * @param {{ cep: string, cidade: string, uf: string }} dados
   */
  #mostrarResultado(dados) {
    const { limite } = this.#resolverLimite(dados.cep);
    const total = this.totalCarrinho;
    const atingiu = total >= limite;
    const faltam = Math.max(limite - total, 0);
    const progresso = limite > 0 ? Math.min(Math.round((total / limite) * 100), 100) : 0;

    this.dataset.state = 'result';
    this.dataset.reached = String(atingiu);

    if (this.refs.bar) this.refs.bar.style.inlineSize = `${progresso}%`;

    if (this.refs.track) {
      this.refs.track.setAttribute('aria-valuenow', String(progresso));
      this.refs.track.setAttribute(
        'aria-valuetext',
        atingiu
          ? 'Frete grátis conquistado'
          : `Faltam ${this.#dinheiro(faltam)} para o frete grátis`
      );
    }

    if (this.refs.message) {
      this.refs.message.innerHTML = atingiu
        ? '<strong>FRETE GRÁTIS LIBERADO</strong>'
        : `Faltam <strong>${this.#dinheiro(faltam)}</strong> para o frete grátis`;
    }

    // Anuncia só quando o patamar muda (conquistou / voltou a faltar), nunca a
    // cada centavo. A progressbar não é live region de propósito.
    if (this.#ultimoPatamar !== atingiu) {
      this.#ultimoPatamar = atingiu;
      this.#anunciar(
        atingiu ? 'Frete grátis liberado' : `Faltam ${this.#dinheiro(faltam)} para o frete grátis`
      );
    }

    if (this.refs.place) {
      const local = dados.cidade
        ? `${dados.cidade}${dados.uf ? `/${dados.uf}` : ''}`
        : this.#formatarCep(dados.cep);
      // "estimativa" fica aqui em vez de numa linha própria: mesma ressalva,
      // uma faixa de altura a menos no drawer.
      this.refs.place.textContent = `Estimativa para ${local}`;
    }
  }

  /**
   * @param {string} texto
   */
  #anunciar = debounce((texto) => {
    if (this.refs.announcer) this.refs.announcer.textContent = texto;
  }, ATRASO_ANUNCIO);

  /**
   * @param {string} texto
   */
  #mostrarErro(texto) {
    if (!this.refs.error) return;
    this.refs.error.textContent = texto;
    this.refs.error.hidden = false;
    this.refs.input?.focus();
  }

  /**
   * @param {number} centavos
   * @returns {string}
   */
  #dinheiro(centavos) {
    return formatMoney(centavos, this.formatoMoeda, this.moeda);
  }

  /**
   * Índice logo após o N-ésimo dígito do texto — usado para recolocar o caret
   * na mesma posição lógica depois que a máscara reescreve o campo.
   * @param {string} texto
   * @param {number} quantidade
   * @returns {number}
   */
  #posicaoDoDigito(texto, quantidade) {
    if (quantidade <= 0) return 0;

    let vistos = 0;
    for (let i = 0; i < texto.length; i++) {
      if (texto[i] >= '0' && texto[i] <= '9') {
        vistos += 1;
        if (vistos === quantidade) return i + 1;
      }
    }

    return texto.length;
  }

  /**
   * @param {string} digitos
   * @returns {string}
   */
  #formatarCep(digitos) {
    const limpo = digitos.replace(/\D/g, '').slice(0, 8);
    return limpo.length > 5 ? `${limpo.slice(0, 5)}-${limpo.slice(5)}` : limpo;
  }

  /**
   * @returns {{ cep: string, cidade: string, uf: string } | null}
   */
  #lerArmazenado() {
    try {
      const bruto = localStorage.getItem(STORAGE_KEY);
      if (!bruto) return null;

      const dados = JSON.parse(bruto);
      if (typeof dados?.cep !== 'string' || dados.cep.length !== 8) return null;

      return { cep: dados.cep, cidade: dados.cidade ?? '', uf: dados.uf ?? '' };
    } catch (_) {
      return null;
    }
  }

  /**
   * @param {{ cep: string, cidade: string, uf: string }} dados
   */
  #gravar(dados) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dados));
    } catch (_) {
      // Modo privado ou storage cheio: o estado vale só para esta visita.
    }
  }
}

if (!customElements.get('cart-freight-bar')) {
  customElements.define('cart-freight-bar', CartFreightBar);
}
